import classNames from 'classnames';
import React, {
  FC, useContext, useEffect, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { FileManagerContext } from '../context';
import { FileManagerCreateFolderModal } from '../modals/createFolder';
import { FileManagerDeleteModal } from '../modals/delete';
import { FileManagerRenameModal } from '../modals/rename';
import { FileManagerUploadFileModal } from '../modals/uploadFile';
import Button from 'components/base/Button';
import CustomIcon from 'components/base/CustomIcon';
import { OtherTypes } from 'components/base/CustomIcon/types';
import Tooltip from 'components/base/Tooltip';
import { fileHasPreview } from 'components/record/Form/fileHasPreview';
import useWindowSize from 'hooks/useWindowSize';

export const FileManagerActions: FC<{
  showLabels?: boolean;
}> = ({ showLabels = false }) => {
  const {
    selectedItem,
    onDeleteItem,
    onCreateFolder,
    onRenameItem,
    onUploadFile,
    onDownloadFile,
    onCopy,
    onCut,
    clipboard,
    onPaste,
    onUpdateView,
    onActiveItem,
  } = useContext(FileManagerContext);
  const { mdOrBigger } = useWindowSize();
  const { t } = useTranslation();
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState<boolean>(false);
  const [showRenameModal, setShowRenameModal] = useState<boolean>(false);
  const [showUploadFileModal, setShowUploadFileModal] = useState<boolean>(false);

  useEffect(
    () => {
      if (!mdOrBigger) {
        onUpdateView('detail-view');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mdOrBigger],
  );

  return (
    <React.Fragment>
      <div className={classNames('f-file-manager-actions', { 'show-labels': showLabels })}>
        {mdOrBigger && (
        <div className="f-file-manager-actions-button-group">
          <Tooltip content={t('record.document.createFolder')}>
            <Button
              color="secondary"
              leftAddon={<CustomIcon icon={OtherTypes.FOLDER_PLUS} />}
              text={t('record.document.createFolder')}
              onClick={() => {
                setShowCreateFolderModal(true);
              }}
            />
          </Tooltip>
          <Tooltip content={`${t('common.add')} ${t('common.document')}`}>
            <Button
              color="secondary"
              leftAddon={<CustomIcon icon={OtherTypes.ADD} />}
              text={`${t('common.add')} ${t('common.document')}`}
              onClick={() => {
                setShowUploadFileModal(true);
              }}
            />
          </Tooltip>
        </div>
        )}
        {mdOrBigger && (
        <div className="f-file-manager-actions-button-group">
          <Tooltip content={t('common.delete')}>
            <Button
              color="secondary"
              disabled={!selectedItem}
              leftAddon={<CustomIcon icon={OtherTypes.DELETE} />}
              text={t('common.delete')}
              onClick={() => {
                setShowDeleteModal(true);
              }}
            />
          </Tooltip>
          <Tooltip content={t('record.document.rename')}>
            <Button
              color="secondary"
              disabled={!selectedItem}
              leftAddon={<CustomIcon icon={OtherTypes.EDIT} />}
              text={t('record.document.rename')}
              onClick={() => {
                setShowRenameModal(true);
              }}
            />
          </Tooltip>
        </div>
        )}
        {mdOrBigger && (
        <div className="f-file-manager-actions-button-group">
          <Tooltip content={t('common.copy')}>
            <Button
              color="secondary"
              disabled={!selectedItem}
              leftAddon={<CustomIcon icon={OtherTypes.COPY} />}
              text={t('common.copy')}
              onClick={() => {
                if (selectedItem) {
                  onCopy(selectedItem);
                }
              }}
            />
          </Tooltip>
          <Tooltip content={t('common.cut')}>
            <Button
              color="secondary"
              disabled={!selectedItem}
              leftAddon={<CustomIcon icon={OtherTypes.CUT} />}
              text={t('common.cut')}
              onClick={() => {
                if (selectedItem) {
                  onCut(selectedItem);
                }
              }}
            />
          </Tooltip>
          <Tooltip content={t('common.paste')}>
            <Button
              color="secondary"
              disabled={!clipboard}
              leftAddon={<CustomIcon icon={OtherTypes.PASTE} />}
              text={t('common.paste')}
              onClick={() => {
                onPaste();
              }}
            />
          </Tooltip>
        </div>
        )}
        <div className="f-file-manager-actions-button-group">
          <Tooltip content={t('record.document.download')}>
            <Button
              color="secondary"
              disabled={!selectedItem || (!!selectedItem && selectedItem.type !== 'file')}
              leftAddon={<CustomIcon icon={OtherTypes.DOWNLOAD} />}
              text={t('record.document.download')}
              onClick={() => {
                if (selectedItem) {
                  onDownloadFile(selectedItem);
                }
              }}
            />
          </Tooltip>
          <Tooltip content={t('common.view')}>
            <Button
              color="secondary"
              disabled={!selectedItem
                || selectedItem.type !== 'file'
                || (selectedItem?.mimeType && !fileHasPreview(selectedItem.mimeType))}
              leftAddon={<CustomIcon icon={OtherTypes.VIEW} />}
              text={t('common.view')}
              onClick={() => {
                if (selectedItem) {
                  onActiveItem(selectedItem);
                }
              }}
            />
          </Tooltip>
        </div>
        {mdOrBigger && (
        <div className="f-file-manager-actions-button-group">
          <Tooltip content={t('record.document.iconsView')}>
            <Button
              color="secondary"
              leftAddon={<CustomIcon icon={OtherTypes.ICONS_VIEW} />}
              text={t('record.document.iconsView')}
              onClick={() => {
                onUpdateView('icons-view');
              }}
            />
          </Tooltip>
          <Tooltip content={t('record.document.listView')}>
            <Button
              color="secondary"
              leftAddon={<CustomIcon icon={OtherTypes.LIST_VIEW} />}
              text={t('record.document.listView')}
              onClick={() => {
                onUpdateView('detail-view');
              }}
            />
          </Tooltip>
        </div>
        )}
      </div>
      {selectedItem && (
      <FileManagerDeleteModal
        item={selectedItem}
        show={showDeleteModal}
        onAccept={() => {
          setShowDeleteModal(false);
          onDeleteItem(selectedItem.id);
        }}
        onCancel={() => setShowDeleteModal(false)}
      />
      )}
      <FileManagerCreateFolderModal
        show={showCreateFolderModal}
        onAccept={(name: string) => {
          onCreateFolder(name);
          setShowCreateFolderModal(false);
        }}
        onCancel={() => {
          setShowCreateFolderModal(false);
        }}
      />
      {
        selectedItem && (
        <FileManagerRenameModal
          show={showRenameModal}
          onAccept={(newName: string) => {
            onRenameItem(selectedItem.id, newName);
            setShowRenameModal(false);
          }}
          onCancel={() => {
            setShowRenameModal(false);
          }}
        />
        )
      }
      <FileManagerUploadFileModal
        show={showUploadFileModal}
        onAccept={(file, filename) => {
          onUploadFile(file, filename);
          setShowUploadFileModal(false);
        }}
        onCancel={() => { setShowUploadFileModal(false); }}
      />
    </React.Fragment>
  );
};
